import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';

// icons
import { PiSquaresFourThin } from "react-icons/pi";
import { FaWallet } from "react-icons/fa";
import { AiOutlineStock } from "react-icons/ai";
import { IoIosSettings } from "react-icons/io";
import { MdHelp } from "react-icons/md";
import { IoLogOut } from "react-icons/io5";
import { FaBalanceScale } from "react-icons/fa";
import { IoChevronBack, IoChevronForward } from "react-icons/io5"; // For collapse/expand toggle

interface Props { }

function SideBar(props: Props) {
  const [collapsed, setCollapsed] = useState(false);

  const menuList = [
    {
      to: "/",
      icon: <PiSquaresFourThin />,
      label: "Dashboard"
    },
    {
      to: "/customer",
      icon: <FaWallet />,
      label: "Customer"
    },
    {
      to: "/company",
      icon: <AiOutlineStock />,
      label: "Company"
    },
    {
      to: "/InvoiceCreator",
      icon: <FaBalanceScale />,
      label: "Invoice Creator"
    },
  ];

  const generalList = [
    {
      to: "/setting",
      icon: <IoIosSettings />,
      label: "Settings"
    },
    {
      to: "/logout",
      icon: <IoLogOut />,
      label: "Logout"
    }
  ];

  // NEW COLOR SCHEME: Soft dark blue and indigo tones, violets, soft focus shadow/highlight, vibrant accent
  // Backgrounds: slate/indigo, Accent: #6D28D9 (purple), Muted: #475569, Soft hover: #EEF2FF, Active: #DDD6FE
  const MAIN_BG = "bg-[#f8fafc]";
  const MAIN_BORDER = "border-[#e0e7ef]";
  const ACCENT_VIOLET = "#6D28D9";
  const MUTED_TEXT = "text-[#475569]";
  const ACTIVE_BG = "bg-[#ddd6fe]";
  const HOVER_BG = "hover:bg-[#eef2ff]";
  const ACTIVE_ICON_BG = "bg-[#6D28D9]";
  const ACTIVE_ICON_COLOR = "text-white";
  const ICON_DEFAULT = "text-[#6D28D9]";
  const ICON_HOVER = "group-hover:text-[#4c1d95]";
  const ACTIVE_LABEL = "text-[#4c1d95]";
  const ACTIVE_LABEL_FONT = "font-bold";
  const SECTION_HEADER = "text-[#5b4e7e]";
  const SECTION_HEADER_MUTED = "text-[#b8b6d6]";

  return (
    <aside
      className={
        `sticky top-0 z-40 bottom-0 h-screen shadow-2xl ${MAIN_BG} transition-all duration-300
        ${collapsed
          ? "w-[64px] pl-2 pr-2"
          : "w-[220px] md:w-[200px] lg:w-[18%] pl-4 pr-2"
        } flex flex-col justify-between border-r ${MAIN_BORDER} pt-0`
      }
      style={{
        boxShadow: "0 6px 32px 0 #ede9fe, 4px 0 24px 0 #f5f3ff inset"
      }}
    >
      <div className="flex-1 flex flex-col justify-between min-h-0">
        <div className="flex flex-col min-h-0">
          {/* Collapse/Expand button */}
          <div className="flex items-center justify-end mb-2 mt-4 mr-2">
            <button
              className="rounded-full border-2 border-[#d1c4e9] bg-white hover:bg-[#ede9fe] shadow p-1.5 transition-all duration-200"
              onClick={() => setCollapsed((c) => !c)}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              type="button"
            >
              {collapsed
                ? <IoChevronForward className="text-xl text-[#6D28D9]" />
                : <IoChevronBack className="text-xl text-[#6D28D9]" />
              }
            </button>
          </div>
          <p className={`text-xs font-semibold tracking-widest ${SECTION_HEADER} ml-2 mb-2 uppercase transition-opacity duration-200 ${collapsed ? 'opacity-0 w-0 h-0 overflow-hidden' : 'opacity-95'}`}>Menu</p>
          <nav className="flex flex-col gap-1">
            {menuList.map(({ to, icon, label }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  `group flex items-center gap-3 py-3 pl-2 pr-2 rounded-lg
                  transition-all
                  ${isActive
                    ? `${ACTIVE_BG} shadow`
                    : `${HOVER_BG} hover:shadow`
                  }
                  ${collapsed ? "justify-center px-0 rounded-full" : ""}`
                }
              >
                {({ isActive }) => (
                  <>
                    <div
                      className={`min-w-[38px] min-h-[38px] flex items-center justify-center transition-all duration-200 rounded-md 
                        ${isActive
                          ? `${ACTIVE_ICON_BG} shadow scale-105`
                          : "bg-white border border-[#d6bcfa] scale-100"
                        }
                      `}
                      style={{
                        boxShadow: isActive ? "0 4px 14px 0 #c4b5fd" : undefined,
                        border: isActive ? "none" : undefined
                      }}
                    >
                      <span className={`${isActive ? ACTIVE_ICON_COLOR : ICON_DEFAULT + ' ' + ICON_HOVER} text-xl`}>
                        {icon}
                      </span>
                    </div>
                    <span
                      className={
                        `whitespace-nowrap ml-1 font-medium transition-all duration-200
                        ${collapsed ? 'opacity-0 w-0 h-0 overflow-hidden' : 'opacity-100'}
                        ${isActive ? ACTIVE_LABEL + ' ' + ACTIVE_LABEL_FONT : `${MUTED_TEXT} group-hover:text-[#6D28D9]`}
                        text-[16px]`
                      }
                      style={collapsed ? { width: 0, padding: 0, margin: 0 } : { letterSpacing: ".01rem" }}
                    >
                      {label}
                    </span>
                  </>
                )}
              </NavLink>
            ))}
          </nav>
        </div>

        {/* Ensures GENERAL section is always visible on small screens */}
        <div className="pt-1 pb-6 border-t border-[#e6e6f0] mt-6 min-h-[1px]">
          <p className={`text-xs font-semibold tracking-widest ${SECTION_HEADER_MUTED} ml-2 uppercase mb-2 transition-all duration-200 ${collapsed ? 'opacity-0 w-0 h-0 overflow-hidden' : 'opacity-85'}`}>General</p>
          <nav className="flex flex-col gap-1">
            {generalList.map(({ to, icon, label }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  `group flex items-center gap-3 py-3 pl-2 pr-2 rounded-lg
                  transition-all
                  ${isActive
                    ? "bg-[#f3e8ff] shadow"
                    : "hover:bg-[#f5f3ff] hover:shadow"
                  }
                  ${collapsed ? "justify-center px-0 rounded-full" : ""}
                  `
                }
              >
                {({ isActive }) => (
                  <>
                    <div
                      className={`min-w-[38px] min-h-[38px] flex items-center justify-center transition-all duration-200 rounded-md 
                        ${isActive
                          ? "bg-[#a78bfa] shadow scale-105"
                          : "bg-white border border-[#e0e7ef] scale-100"
                        }
                      `}
                      style={{
                        boxShadow: isActive ? "0 2px 8px 0 #ddd6fe" : undefined,
                        border: isActive ? "none" : undefined
                      }}
                    >
                      <span className={`${isActive ? 'text-[#36306b]' : 'text-[#9a86df] group-hover:text-[#6D28D9]'} text-xl`}>
                        {icon}
                      </span>
                    </div>
                    <span
                      className={
                        `whitespace-nowrap ml-1 font-medium transition-all duration-200
                        ${collapsed ? 'opacity-0 w-0 h-0 overflow-hidden' : 'opacity-100'}
                        ${isActive ? 'text-[#6D28D9] font-semibold' : 'text-[#b8b6d6] group-hover:text-[#6D28D9]'}
                        text-[15px]`
                      }
                      style={collapsed ? { width: 0, padding: 0, margin: 0 } : { letterSpacing: ".01rem" }}
                    >
                      {label}
                    </span>
                  </>
                )}
              </NavLink>
            ))}
          </nav>
        </div>
      </div>
    </aside>
  );
}

export default SideBar
